import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { func, object, bool, string } from 'prop-types';

import { useGeoOSM } from '../../hooks/useGeoOSM';
import { useGeoGSV } from '../../hooks/useGeoGSV';
import { ALERT_TYPE, ShowToastMessage, useForm, useGeoLocal } from '@laramie/ui';
import { useFormErrors } from '../../hooks/useFormErrors';
import { useLista } from '../../hooks/useLista';
import { useEntidad } from '../../hooks/useEntidad';
import { useConfiguracion } from '../../hooks/useConfiguracion';
import { CloneObject } from '../../../utils/helpers';
import { InputLista, InputEntidad, MessageModal, GeoLoadingModal, FormLabel, Select, FormError } from '../../common';
import { VALIDATION_MESSAGE } from '../../../consts/validationMessage';
import { APPCONFIG } from '../../../app.config';
import Maps from '../../common/Maps';

import './index.scss';
import {TIPS} from "../../../consts/tips";

//export method
export let GetDataDireccion = new Map();
export let IsValidDireccion = new Map();
export let IsEmptyDireccion = new Map();

const DireccionForm = forwardRef((props, ref) => {

  if (props.id) {
    GetDataDireccion.set(props.id, () => getFormData());
    IsValidDireccion.set(props.id, () => isFormValid(false));
    IsEmptyDireccion.set(props.id, () => isFormEmpty());

  }

  const locationInit = {
    lat: parseFloat(APPCONFIG.MAPS.LAT),
    lng: parseFloat(APPCONFIG.MAPS.LNG)
  }

  const entityInit = {
    id: 0,
    entidad: "",
    idEntidad: 0,
    idTipoGeoreferencia: 0,
    idPais: 0,
    idProvincia: 0,
    idLocalidad: 0,
    idZonaGeoreferencia: 0,
    codigoPostal: "",
    calle: "",
    entreCalle1: "",
    entreCalle2: "",
    altura: "",
    piso: "",
    dpto: "",
    referencia: "",
    longitud: 0,
    latitud: 0,
    origen: ""
  };

  //hooks
  const [direccionCodigoPostalObligatorio, , readyDireccionCodigoPostalObligatorio] = useConfiguracion({ key: 'DireccionCodigoPostalObligatorio' });
  //hasta que resuelve la configuración se asume obligatorio, que es el comportamiento por defecto
  const codigoPostalObligatorio = !readyDireccionCodigoPostalObligatorio || direccionCodigoPostalObligatorio === 'SI';

  const [state, setState] = useState({
    entity: entityInit,
    showGeoLoading: false,
    showMessage: false,
    textMessage: "",
    modeFormEdit: false
  });

  const [location, setLocation] = useState(locationInit);

  const [valueTipoGeoreferencia, setTipoGeoreferencia] = useState({listTipoGeoreferencia: [], indexTipoGeoreferencia: 0});

  const [valuePais, setPais] = useState({nombrePais: ''});
  const [valueProvincia, setProvincia] = useState({nombreProvincia: ''});
  const [valueLocalidad, setLocalidad] = useState({nombreLocalidad: ''});

  const [copy, setCopy] = useState({form: null, location: null});


  const mount = () => {
    setState(prevState => {
      return {...prevState, entity: props.data.entity, modeFormEdit: !props.disabled && props.initFormEdit};
    });
    formSet({
      idTipoGeoreferencia: props.data.entity.idTipoGeoreferencia,
      idPais: props.data.entity.idPais,
      idProvincia: props.data.entity.idProvincia,
      idLocalidad: props.data.entity.idLocalidad,
      idZonaGeoreferencia: props.data.entity.idZonaGeoreferencia ?? 0,
      codigoPostal: props.data.entity.codigoPostal,
      calle: props.data.entity.calle,
      entreCalle1: props.data.entity.entreCalle1,
      entreCalle2: props.data.entity.entreCalle2,
      altura: props.data.entity.altura,
      piso: props.data.entity.piso,
      dpto: props.data.entity.dpto,
      referencia: props.data.entity.referencia
    });
    setLocation({
      lat: props.data.entity.latitud,
      lng: props.data.entity.longitud
    });
  }
  useEffect(mount, [props.data.entity, props.data.entity.idTipoGeoreferencia]);

  const [ formValues, formHandle, , formSet ] = useForm({
    idTipoGeoreferencia: 0,
    idPais: 0,
    idProvincia: 0,
    idLocalidad: 0,
    idZonaGeoreferencia: 0,
    codigoPostal: "",
    calle: "",
    entreCalle1: "",
    entreCalle2: "",
    altura: "",
    piso: "",
    dpto: "",
    referencia: ""
  });

  useImperativeHandle(ref, () => ({
    getDataDireccion: getFormData,
    isValidDireccion: isFormValid,
    IsEmptyDireccion: isFormEmpty,
  }))

  //Open Street Map
  const [ locationOSM, setAddressOSM ] = useGeoOSM();
  useEffect(() => {
    if (locationOSM.status === 3 || locationOSM.status === 2) {
      if (autoGeocodingRef.current) {
        // Fallo de auto-geocoding (ej: ARCA no mapea a una dirección geocodificable):
        // avisar al usuario y dejar el form en modo edición para que corrija
        ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'No se pudo georreferenciar automáticamente la dirección de ARCA. Verifique y georreferencie manualmente.');
        setState(prevState => {return {...prevState, showMessage: true, textMessage: createMessage()};});
      } else {
        if (locationOSM.status === 3) ShowToastMessage(ALERT_TYPE.ALERT_WARNING, locationOSM.message);
        setState(prevState => {return {...prevState, showMessage: true, textMessage: createMessage()};});
      }
    }
    else if (locationOSM.status === 1) {
      if ((locationOSM.lat !== 0 && location.lat !== locationOSM.lat) || (locationOSM.lng !== 0 && location.lng !== locationOSM.lng)) {
        setLocation({lat: locationOSM.lat, lng: locationOSM.lng});
      }
      if (locationOSM.street)
        formSet({...formValues, calle: locationOSM.street})
      confirmEdit();
    }
  }, [locationOSM]);

  //GSV Street View
  const [ locationGSV, setAddressGSV ] = useGeoGSV();
  useEffect(() => {
    if (locationGSV.status === 3) {
      ShowToastMessage(ALERT_TYPE.ALERT_WARNING, locationGSV.message);
      setState(prevState => {return {...prevState, showMessage: true, textMessage: createMessage()};});
    }
    else if (locationGSV.status === 2) {
      setState(prevState => {return {...prevState, showMessage: true, textMessage: createMessage()};});
    }
    else if (locationGSV.status === 1) {
      if (locationGSV.lat !== 0 && location.lat !== locationGSV.lat || locationGSV.lng !== 0 && location.lng !== locationGSV.lng) {
        setLocation({lat: locationGSV.lat, lng: locationGSV.lng});
      }
      confirmEdit();
    }
  }, [locationGSV]);

  //API Local
  const [ locationLocal, setAddressLocal ] = useGeoLocal();
  useEffect(() => {
    if (locationLocal.status === 3) {
      ShowToastMessage(ALERT_TYPE.ALERT_WARNING, locationLocal.message);
      setState(prevState => {return {...prevState, showMessage: true, textMessage: createMessage()};});
    }
    else if (locationLocal.status === 2) {
      setState(prevState => {return {...prevState, showMessage: true, textMessage: createMessage()};});
    }
    else if (locationLocal.status === 1) {
      if (locationLocal.lat !== 0 && location.lat !== locationLocal.lat || locationLocal.lng !== 0 && location.lng !== locationLocal.lng) {
        setLocation({lat: locationLocal.lat, lng: locationLocal.lng});
      }
      confirmEdit();
    }
  }, [locationLocal]);

  //Carga por Zona
  const [ getListZonaGeoreferencia, getRowZonaGeoreferencia ] = useEntidad({
    entidades: ['ZonaGeoreferencia'],
    onLoaded: (entidades, isSuccess, error) => {
      if (isSuccess) {
        setState(prevState => ({...prevState}));
      }
      else {
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
      }
    },
    memo: {
      key: 'ZonaGeoreferencia',
      timeout: 0
    }
  });
  //Secuencia de Tipo Georeferencia
  const [ getListTipoGeoreferencia, ] = useLista({
    listas: ['TipoGeoreferencia'],
    onLoaded: (listas, isSuccess, error) => {
      if (isSuccess) {
        const listTipoGeoreferencia = getListTipoGeoreferencia('TipoGeoreferencia');
        setTipoGeoreferencia({listTipoGeoreferencia: listTipoGeoreferencia, indexTipoGeoreferencia: 0});
      }
      else {
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
      }
    },
    memo: {
      key: 'TipoGeoreferencia',
      timeout: 0
    }
  });

  // Auto-geocoding: cuando se monta con dirección pre-resuelta (ej: ARCA), geocodificar inmediatamente
  // Usa props.autoGeocodeAddress (texto directo) para no depender de la resolución async de InputEntidad
  const autoGeocodingRef = useRef(false);
  useEffect(() => {
    if (!props.autoGeocode || autoGeocodingRef.current) return;
    const addr = props.autoGeocodeAddress;
    if (!addr || !addr.street) return;
    autoGeocodingRef.current = true;
    setAddressOSM({
      country: addr.country || '',
      state: addr.state || '',
      city: addr.city || addr.state || '', // fallback: CABA no tiene localidad separada en ARCA
      town: '',
      street: addr.street,
      number: addr.number || ''
    });
  }, [props.autoGeocode, props.autoGeocodeAddress, setAddressOSM]);

  //function
  const getFormData = () => {
    let row = CloneObject(state.entity);
    row.idTipoGeoreferencia = parseInt(formValues.idTipoGeoreferencia);
    row.idPais = parseInt(formValues.idPais);
    row.idProvincia = parseInt(formValues.idProvincia);
    row.idLocalidad = parseInt(formValues.idLocalidad);
    row.codigoPostal = formValues.codigoPostal;

    row.calle = formValues.calle;
    row.entreCalle1 = formValues.entreCalle1;
    row.entreCalle2 = formValues.entreCalle2;
    row.altura = formValues.altura;
    row.piso = formValues.piso;
    row.dpto = formValues.dpto;

    row.idZonaGeoreferencia = parseInt(formValues.idZonaGeoreferencia);
    row.referencia = formValues.referencia;

    row.longitud = location.lng;
    row.latitud = location.lat;

    return row;
  }

  const getErrors = () => {

    const errors = {};

    if (formValues.idPais <= 0) errors.idPais = VALIDATION_MESSAGE.REQUIRED_OPTION;
    if (formValues.idProvincia <= 0) errors.idProvincia = VALIDATION_MESSAGE.REQUIRED_OPTION;
    if (formValues.idLocalidad <= 0) errors.idLocalidad = VALIDATION_MESSAGE.REQUIRED_OPTION;
    if (codigoPostalObligatorio && formValues.codigoPostal.length <= 0) errors.codigoPostal = VALIDATION_MESSAGE.REQUIRED_SHORT;

    if ([530,531,532].includes(formValues.idTipoGeoreferencia)) { //con api o carga directa
      if (formValues.calle.length <= 0) errors.calle = VALIDATION_MESSAGE.REQUIRED;
      if (formValues.altura.length <= 0) errors.altura = VALIDATION_MESSAGE.REQUIRED_SHORT;
    }
    if ([533].includes(formValues.idTipoGeoreferencia)) { //con zona
      if (formValues.idZonaGeoreferencia <= 0) errors.idZonaGeoreferencia = VALIDATION_MESSAGE.REQUIRED_OPTION;
    }
    if ([533,534].includes(formValues.idTipoGeoreferencia)) { //con zona o carga manual
      if (formValues.referencia <= 0) errors.referencia = VALIDATION_MESSAGE.REQUIRED;
    }

    return errors;
  }

  const { errorOf, validate } = useFormErrors(getErrors);

  const isFormValid = (partial) => {

    if (!partial && state.modeFormEdit) {
      return {result: false, message: 'Debe finalizar el proceso de geolocalización primero'};
    }

    if (!validate()) {
      return {result: false, message: null};
    }

    if (!partial && (location.lat === 0 || location.lng === 0)) {
      return {result: false, message: 'Debe georreferenciar la ubicación'};
    }

    return {result: true, message: null};
  }

  const isFormEmpty = () => {

    if (formValues.idPais > 0) {
      return false
    }
    if (formValues.idProvincia > 0) {
      return false;
    }
    if (formValues.idLocalidad > 0) {
      return false;
    }
    if (formValues.codigoPostal.length > 0) {
      return false;
    }
    
    if ([530,531,532].includes(formValues.idTipoGeoreferencia)) { //con api o carga directa
      if (formValues.calle.length > 0) {
        return false;
      }
      if (formValues.altura.length > 0) {
        return false;
      }
    }
    if ([533].includes(formValues.idTipoGeoreferencia)) { //con zona
      if (formValues.idZonaGeoreferencia > 0) {
        return false;
      }
    }
    if ([533,534].includes(formValues.idTipoGeoreferencia)) { //con zona o carga manual
      if (formValues.referencia > 0) {
        return false;
      }
    }

    if (location.lat > 0 || location.lng > 0) {
      return false;
    }

    return true;
  }

  const executeGeoreferencia = () => {
    autoGeocodingRef.current = false; // acción manual del usuario: habilitar fallback chain
    if (formValues.idTipoGeoreferencia === 530) //Open Street Map
    {
      setAddressOSM({
        country: valuePais.nombrePais,
        state: valueProvincia.nombreProvincia,
        city: valueLocalidad.nombreLocalidad,
        town: '',
        street: formValues.calle,
        number: formValues.altura
      });
    }
    else if (formValues.idTipoGeoreferencia === 531) //GSV Street View
    {
      setAddressGSV({
        country: valuePais.nombrePais,
        state: valueProvincia.nombreProvincia,
        city: valueLocalidad.nombreLocalidad,
        town: '',
        street: formValues.calle,
        number: formValues.altura
      });
    }
    else if (formValues.idTipoGeoreferencia === 532) //API Local
    {
      setAddressLocal({
        country: valuePais.nombrePais,
        state: valueProvincia.nombreProvincia,
        city: valueLocalidad.nombreLocalidad,
        town: '',
        street: formValues.calle,
        number: formValues.altura
      });
    }

  }

  const confirmEdit = () => {
    if (props.setPendingChange) {
      props.setPendingChange(true);
    }
    setState(prevState => {return {...prevState, modeFormEdit: false};});
    setCopy({form: null, location: null});
  }


  const createGeoLoadingMessage = () => {
    if (valueTipoGeoreferencia.listTipoGeoreferencia.length > 0) {
      const rowNext = valueTipoGeoreferencia.listTipoGeoreferencia[valueTipoGeoreferencia.indexTipoGeoreferencia + 1];
      const message = `Georreferenciando con "${rowNext.nombre}"...`;
      return message;
    }
    else {
      return "Mensaje indefinido";
    }
  }

  const createMessage = () => {
    if (valueTipoGeoreferencia.listTipoGeoreferencia.length > 0) {
      const rowCurrent = valueTipoGeoreferencia.listTipoGeoreferencia[valueTipoGeoreferencia.indexTipoGeoreferencia];
      const rowNext = valueTipoGeoreferencia.listTipoGeoreferencia[valueTipoGeoreferencia.indexTipoGeoreferencia + 1];
      const message = `"${rowCurrent.nombre}" no ha podido encontrar la ubicación. ¿Desea intentar con "${rowNext.nombre}"?`;
      return message;
    }
    else {
      return "Mensaje indefinido";
    }
  }

  const resetTipoGeoreferencia = () => {
    setTipoGeoreferencia(prevState => {return {...prevState, indexTipoGeoreferencia: 0}});
    const idTipoGeoreferencia = valueTipoGeoreferencia.listTipoGeoreferencia[0].id;
    changeTipoGeoreferencia(idTipoGeoreferencia);
  }

  const nextTipoGeoreferencia = () => {
    if (valueTipoGeoreferencia.indexTipoGeoreferencia < valueTipoGeoreferencia.listTipoGeoreferencia.length) {
      const nextIndex = valueTipoGeoreferencia.indexTipoGeoreferencia + 1;
      setTipoGeoreferencia(prevState => {return {...prevState, indexTipoGeoreferencia: nextIndex}});
      const idTipoGeoreferencia = valueTipoGeoreferencia.listTipoGeoreferencia[nextIndex].id;
      changeTipoGeoreferencia(idTipoGeoreferencia);

      if([530,531,532].includes(idTipoGeoreferencia)) {
        setState(prevState => {return {...prevState, showGeoLoading: true, textMessage: createGeoLoadingMessage()};});
      }
    }
  }

  const changeTipoGeoreferencia = (idTipoGeoreferencia) => {
    let formValuesCleaned = CloneObject(formValues);
    formValuesCleaned.idTipoGeoreferencia = idTipoGeoreferencia;

    if ([530,531,532].includes(idTipoGeoreferencia)) { //con api
      formValuesCleaned.idZonaGeoreferencia = 0;
      formValuesCleaned.referencia = "";
    }
    else if ([533].includes(idTipoGeoreferencia)) { //con zona
      formValuesCleaned.calle = "";
      formValuesCleaned.entreCalle1 = "";
      formValuesCleaned.entreCalle2 = "";
      formValuesCleaned.altura = "";
      formValuesCleaned.piso = "";
      formValuesCleaned.dpto = "";
    }
    else if ([534].includes(idTipoGeoreferencia)) { //carga manual
      formValuesCleaned.idZonaGeoreferencia = 0;
      formValuesCleaned.calle = "";
      formValuesCleaned.entreCalle1 = "";
      formValuesCleaned.entreCalle2 = "";
      formValuesCleaned.altura = "";
      formValuesCleaned.piso = "";
      formValuesCleaned.dpto = "";
      setLocation(locationInit);
    }

    formSet(formValuesCleaned);
  }


  const callbackGeoLoading = () => {
    handleClickConfirmEdit();
    setState(prevState => {return {...prevState, showGeoLoading: false};});
  }

  //handle
 
  const handleChangeZonaGeoreferencia = ({ target }) => {
    const idZonaGeoreferencia = parseInt(target.value);
    if (idZonaGeoreferencia > 0) {
      const rowZonaGeoreferencia = getRowZonaGeoreferencia('ZonaGeoreferencia',idZonaGeoreferencia);
      formSet({...formValues, idZonaGeoreferencia: idZonaGeoreferencia});
      const newLocation = {lat: rowZonaGeoreferencia.latitud, lng: rowZonaGeoreferencia.longitud};
      setLocation(newLocation);
    }
    else {
      formSet({...formValues, idZonaGeoreferencia: 0});
    }
  }

  const handleChangeTipoGeoreferencia = ({ target }) => {
    const idTipoGeoreferencia = parseInt(target.value);
    changeTipoGeoreferencia(idTipoGeoreferencia);
  }


  const handleClickInitEdit = () => {
    setCopy({
      form: CloneObject(formValues),
      location: CloneObject(location)
    });
    setState(prevState => {return {...prevState, modeFormEdit: true};});
    resetTipoGeoreferencia();
  }

  const handleClickConfirmEdit = () => {
    const validation = isFormValid(true);
    if (!validation.result)
    {
      if (validation.message) ShowToastMessage(ALERT_TYPE.ALERT_WARNING, validation.message);
      return;
    }

    if ([530,531,532].includes(formValues.idTipoGeoreferencia)) {
      executeGeoreferencia();
    }
    else {
      confirmEdit();
    }
  }

  const handleClickDismissEdit = () => {
    if (copy.form) {
      formSet(CloneObject(copy.form));
    }
    if (copy.location) {
      setLocation(CloneObject(copy.location));
    }
    setState(prevState => {return {...prevState, modeFormEdit: false};});
    setCopy({form: null, location: null});
  }

  const handleClickCargaManual = () => {
    nextTipoGeoreferencia();
  }


  return (
    <>

    {state.showGeoLoading && 
        <GeoLoadingModal
            active={state.showGeoLoading}
            message={state.textMessage}
            callbackGeoLoading={callbackGeoLoading}
        />
    }

    {state.showMessage && 
        <MessageModal
            title={"Aviso"}
            message={state.textMessage}
            onDismiss={() => {
              setState(prevState => {return {...prevState, showMessage: false};});
            }}
            onConfirm={() => {
              setState(prevState => {return {...prevState, showMessage: false};});
              nextTipoGeoreferencia();
            }}
        />
    }

    <div className='row direccion-form'>
      <div className='col-6'>
      <div className='row m-top-10'>
          <div className="col-6 col-lg-6">
              <label htmlFor="idTipoGeoreferencia" className="form-label">Tipo georreferencia</label>
              <InputLista
                  name="idTipoGeoreferencia"
                  placeholder=""
                  className="form-control"
                  value={ formValues.idTipoGeoreferencia }
                  onChange={ handleChangeTipoGeoreferencia }
                  disabled={true}
                  title="Tipo georreferencia"
                  lista="TipoGeoreferencia"
              />
              <FormError />
          </div>

          <div className="col-6 col-lg-6">
              <FormLabel htmlFor="idPais" required>País</FormLabel>
              <InputEntidad
                  name="idPais"
                  placeholder=""
                  tipName={TIPS.NAME.MODALS.PAIS_MODAL}
                  className={`form-control ${errorOf('idPais') ? 'is-invalid' : ''}`}
                  value={ formValues.idPais }
                  onChange={({target}) => {
                    formSet({...formValues,
                      idPais: target.value,
                      idProvincia: 0,
                      idLocalidad: 0
                    });
                  }}
                  onUpdate={({target}) => {
                    setPais({nombrePais: (target.row) ? target.row.nombre: ''});
                  }}
                  disabled={props.disabled || !state.modeFormEdit || valueTipoGeoreferencia.indexTipoGeoreferencia > 0}
                  title="País"
                  entidad="Pais"
                  columns= {[{ Header: 'Nombre', accessor: 'nombre', width: '95%' }]}
              />
              <FormError message={errorOf('idPais')} />
          </div>
          <div className="col-6 col-lg-6">
              <FormLabel htmlFor="idProvincia" required>Provincia</FormLabel>
              <InputEntidad
                  name="idProvincia"
                  placeholder=""
                  tipName={TIPS.NAME.MODALS.PROVINCIA_MODAL}
                  className={`form-control ${errorOf('idProvincia') ? 'is-invalid' : ''}`}
                  value={ formValues.idProvincia }
                  onChange={({target}) => {
                    formSet({...formValues,
                      idProvincia: target.value,
                      idLocalidad: 0
                    });
                  }}
                  onUpdate={({target}) => {
                    setProvincia({nombreProvincia: (target.row) ? target.row.nombre: ''});
                  }}
                  disabled={props.disabled || !state.modeFormEdit || valueTipoGeoreferencia.indexTipoGeoreferencia > 0}
                  title="Provincia"
                  entidad="Provincia"
                  filter={(row) => row.idPais === formValues.idPais}
                  columns= {[{ Header: 'Nombre', accessor: 'nombre', width: '95%' }]}
              />
              <FormError message={errorOf('idProvincia')} />
          </div>
          <div className="col-6 col-lg-6">
              <FormLabel htmlFor="idLocalidad" required>Localidad</FormLabel>
              <InputEntidad
                  name="idLocalidad"
                  placeholder=""
                  tipName={TIPS.NAME.MODALS.LOCALIDAD_MODAL}
                  className={`form-control ${errorOf('idLocalidad') ? 'is-invalid' : ''}`}
                  value={ formValues.idLocalidad }
                  onChange={({target}) => {
                    formSet({...formValues,
                      idLocalidad: target.value
                    });
                  }}
                  onUpdate={({target}) => {
                    setLocalidad({nombreLocalidad: (target.row) ? target.row.nombre: ''});
                  }}
                  disabled={props.disabled || !state.modeFormEdit || valueTipoGeoreferencia.indexTipoGeoreferencia > 0}
                  title="Localidad"
                  entidad="Localidad"
                  filter={(row) => row.idProvincia === formValues.idProvincia}
                  columns= {[{ Header: 'Nombre', accessor: 'nombre', width: '95%' }]}
              />
              <FormError message={errorOf('idLocalidad')} />
          </div>
          <div className="col-2 col-md-4">
              <FormLabel htmlFor="codigoPostal" required={codigoPostalObligatorio}>CP</FormLabel>
              <input
                  name="codigoPostal"
                  type="text"
                  placeholder=""
                  className={`form-control ${errorOf('codigoPostal') ? 'is-invalid' : ''}`}
                  value={formValues.codigoPostal }
                  onChange={ formHandle }
                  disabled={props.disabled || !state.modeFormEdit || valueTipoGeoreferencia.indexTipoGeoreferencia > 0}
              />
              <FormError message={errorOf('codigoPostal')} />
          </div>
          {[530,531,532,535].includes(formValues.idTipoGeoreferencia) && (
          <>
          <div className="col-8">
              <FormLabel htmlFor="calle" required={[530,531,532].includes(formValues.idTipoGeoreferencia)}>Calle</FormLabel>
              <input
                  name="calle"
                  type="text"
                  placeholder=""
                  className={`form-control ${errorOf('calle') ? 'is-invalid' : ''}`}
                  value={formValues.calle }
                  onChange={ formHandle }
                  disabled={props.disabled || !state.modeFormEdit || valueTipoGeoreferencia.indexTipoGeoreferencia > 0}
              />
              <FormError message={errorOf('calle')} />
          </div>
          <div className="col-6 col-md-4">
              <FormLabel htmlFor="altura" required={[530,531,532].includes(formValues.idTipoGeoreferencia)}>Altura</FormLabel>
              <input
                  name="altura"
                  type="text"
                  placeholder=""
                  className={`form-control ${errorOf('altura') ? 'is-invalid' : ''}`}
                  value={formValues.altura }
                  onChange={ formHandle }
                  disabled={props.disabled || !state.modeFormEdit || valueTipoGeoreferencia.indexTipoGeoreferencia > 0}
              />
              <FormError message={errorOf('altura')} />
          </div>
          <div className="col-6 col-md-4">
              <label htmlFor="piso" className="form-label">Piso</label>
              <input
                  name="piso"
                  type="text"
                  placeholder=""
                  className="form-control"
                  value={formValues.piso }
                  onChange={ formHandle }
                  disabled={props.disabled || !state.modeFormEdit || valueTipoGeoreferencia.indexTipoGeoreferencia > 0}
              />
              <FormError />
          </div>
          <div className="col-6 col-md-4">
              <label htmlFor="dpto" className="form-label">Dpto</label>
              <input
                  name="dpto"
                  type="text"
                  placeholder=""
                  className="form-control"
                  value={formValues.dpto }
                  onChange={ formHandle }
                  disabled={props.disabled || !state.modeFormEdit || valueTipoGeoreferencia.indexTipoGeoreferencia > 0}
              />
              <FormError />
          </div>
          <div className="col-12 col-md-6">
              <label htmlFor="entreCalle1" className="form-label">Entre Calle (1)</label>
              <input
                  name="entreCalle1"
                  type="text"
                  placeholder=""
                  className="form-control"
                  value={formValues.entreCalle1 }
                  onChange={ formHandle }
                  disabled={props.disabled || !state.modeFormEdit || valueTipoGeoreferencia.indexTipoGeoreferencia > 0}
              />
              <FormError />
          </div>
          <div className="col-12 col-md-6">
              <label htmlFor="entreCalle2" className="form-label">Entre Calle (2)</label>
              <input
                  name="entreCalle2"
                  type="text"
                  placeholder=""
                  className="form-control"
                  value={formValues.entreCalle2 }
                  onChange={ formHandle }
                  disabled={props.disabled || !state.modeFormEdit || valueTipoGeoreferencia.indexTipoGeoreferencia > 0}
              />
              <FormError />
          </div>
          </>
          )}
          {[533,535].includes(formValues.idTipoGeoreferencia) && (
          <>
          <div className="col-12 col-md-8">
              <FormLabel htmlFor="idZonaGeoreferencia" required={formValues.idTipoGeoreferencia === 533}>Zona</FormLabel>
              <Select
                  name="idZonaGeoreferencia"
                  className={`form-control ${errorOf('idZonaGeoreferencia') ? 'is-invalid' : ''}`}
                  value={ formValues.idZonaGeoreferencia }
                  onChange={ handleChangeZonaGeoreferencia }
                  disabled={props.disabled || !state.modeFormEdit}
                  emptyOption="Sin selección"
                  emptyValue={0}
                  options={getListZonaGeoreferencia('ZonaGeoreferencia').map(item => ({ value: item.id, label: item.nombre }))}
              />
              <FormError message={errorOf('idZonaGeoreferencia')} />
          </div>
          {!props.disabled && state.modeFormEdit && (
          <div className="col-12 col-md-4 m-top-30">
            <button className="btn btn-primary float-end" onClick={ (event) => handleClickCargaManual() }>
              Carga manual
            </button>
          </div>
          )}
          </>
          )}
          {[533,534,535].includes(formValues.idTipoGeoreferencia) && (
          <>
          <div className="col-12">
              <FormLabel htmlFor="referencia" required={[533,534].includes(formValues.idTipoGeoreferencia)}>Referencia</FormLabel>
              <input
                  name="referencia"
                  type="text"
                  placeholder=""
                  className={`form-control ${errorOf('referencia') ? 'is-invalid' : ''}`}
                  value={formValues.referencia }
                  onChange={ formHandle }
                  disabled={props.disabled || !state.modeFormEdit}
              />
              <FormError message={errorOf('referencia')} />
          </div>
          </>
          )}
      </div>

      <div className='row'>
        <div className="col-12 m-top-10">
          {!props.disabled && !state.modeFormEdit && (
            <button className="btn btn-primary btn-map" onClick={ (event) => handleClickInitEdit() }>
              <span className="material-symbols-outlined" title="modificar dirección">location_on</span>
            </button>
          )}
          {!props.disabled && state.modeFormEdit && (
            <>
            <button className="btn btn-primary btn-map float-end m-left-5" onClick={ (event) => handleClickConfirmEdit() }>
                <span className="material-symbols-outlined" title="aceptar">done</span>
            </button>
            <button className="btn btn-outline-primary btn-map float-end" onClick={ (event) => handleClickDismissEdit() }>
                <span className="material-symbols-outlined" title="cancelar">close</span>
            </button>
            </>
          )}
        </div>
      </div>
      
      </div>
      
      <div className=' m-top-20 col-6'>
        <div className="position-relative z-0">
            
            <Maps
              disabled={!([534,535].includes(formValues.idTipoGeoreferencia) && !props.disabled && state.modeFormEdit)}
              position={location}
              onChange={setLocation}
            />

        </div>
      </div>

    </div>

    </>
  );
})

DireccionForm.propTypes = {
  id: string,
  disabled: bool,
  initFormEdit: bool,
  autoGeocode: bool,
  autoGeocodeAddress: object,
  data: object.isRequired,
  setPendingChange: func
};

DireccionForm.defaultProps = {
  id: null,
  disabled: false,
  initFormEdit: false,
  autoGeocode: false,
  autoGeocodeAddress: null,
  setPendingChange: null
};

export default DireccionForm
